import { ProjectSuggestions } from '@platform/client';
import { DatePicker, Modal, ModalCallToAction, Spinner } from '@platform/components';
import { useState } from 'react';
import { GreenCircleCheckmark } from '../../../svg/GreenCircleCheckmark';
import { useQueryClient } from '@tanstack/react-query';

export const SuggestProjectModal = ({
  organizationId,
  projectSuggestionId,
  projectSuggestionIndex,
  onClose,
}: {
  organizationId: number;
  projectSuggestionId: number;
  projectSuggestionIndex: number;
  onClose: () => void;
}) => {
  const [modalStage, setModalStage] = useState<'confirm' | 'sending' | 'success' | 'error'>(
    'confirm',
  );
  const [scheduledSendDate, setScheduledSendDate] = useState<Date>();
  const queryClient = useQueryClient();
  const suggestProjectToRep = ProjectSuggestions.useSendProjectSuggestEmail({
    mutation: {
      onMutate: () => setModalStage('sending'),
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ProjectSuggestions.getGetProjectSuggestionsQueryKey(organizationId),
        });
        setModalStage('success');
      },
      onError: () => setModalStage('error'),
    },
  });
  const handleSendEmail = () => {
    suggestProjectToRep.mutate({
      params: {
        scheduled_email_date: scheduledSendDate?.toISOString(),
        suggested_project_id: projectSuggestionId,
      },
    });
  };
  const primaryCta: ModalCallToAction = {
    text: scheduledSendDate ? 'Schedule Email' : 'Send Now',
    callback: handleSendEmail,
    isDisabled: () => modalStage === 'sending',
  };
  if (modalStage === 'success' || modalStage === 'error') {
    primaryCta.text = 'Close';
    primaryCta.callback = onClose;
  }
  const secondaryCta: ModalCallToAction | undefined =
    modalStage === 'confirm' ?
      {
        text: 'No, Cancel',
        callback: onClose,
      }
    : undefined;

  return (
    <Modal
      headerDivision='divider'
      isOpen={true}
      onClose={onClose}
      title={`Are you sure you want to email suggestion #${projectSuggestionIndex + 1}?`}
      variant='dialog'
      primaryCTA={primaryCta}
      secondaryCTA={secondaryCta}
      ignoreExtraPadding
    >
      {modalStage === 'confirm' && (
        <>
          <p className='mb-4'>
            You can select a date below to send the email, or leave blank to send it now.
          </p>
          <DatePicker
            label='Schedule send date (optional)'
            onChange={(date) => setScheduledSendDate(date as Date)}
          />
        </>
      )}
      {modalStage === 'sending' && (
        <div className='flex justify-center items-center'>
          <Spinner />
        </div>
      )}
      {modalStage === 'error' && (
        <p className='text-red-500'>
          There was an error suggesting this project. Please try again.
        </p>
      )}
      {modalStage === 'success' && (
        <div className='flex flex-col gap-2 justify-center items-center'>
          <GreenCircleCheckmark />
          <p>Project successfully suggested to rep!</p>
        </div>
      )}
    </Modal>
  );
};
